/**
 * パフォーマンス最適化ユーティリティ
 * 認証システムのパフォーマンス監視と最適化を行う
 */

interface PerformanceMetrics {
  authStateChangeTime: number;
  tokenRefreshTime: number;
  userSyncTime: number;
  componentRenderTime: number;
}

interface OptimizationReport {
  metrics: PerformanceMetrics;
  recommendations: string[];
  issues: string[];
}

class PerformanceOptimizer {
  private metrics: Partial<PerformanceMetrics> = {};
  private startTimes: Map<string, number> = new Map();
  private memoryCheckInterval: number | null = null;
  private lastMemoryUsage: number | null = null;

  /**
   * パフォーマンス測定を開始
   */
  startMeasurement(operation: keyof PerformanceMetrics): void {
    this.startTimes.set(operation, performance.now());
  }

  /**
   * パフォーマンス測定を終了
   */
  endMeasurement(operation: keyof PerformanceMetrics): number {
    const startTime = this.startTimes.get(operation);
    if (!startTime) {
      console.warn(`No start time found for operation: ${operation}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.metrics[operation] = duration;
    this.startTimes.delete(operation);

    return duration;
  }

  /**
   * 認証状態変更のパフォーマンスを測定
   */
  measureAuthStateChange<T>(callback: () => Promise<T>): Promise<T> {
    this.startMeasurement('authStateChangeTime');

    return callback().finally(() => {
      const duration = this.endMeasurement('authStateChangeTime');

      if (duration > 1000) {
        console.warn(`Slow auth state change detected: ${duration}ms`);
      }
    });
  }

  /**
   * トークンリフレッシュのパフォーマンスを測定
   */
  measureTokenRefresh<T>(callback: () => Promise<T>): Promise<T> {
    this.startMeasurement('tokenRefreshTime');

    return callback().finally(() => {
      const duration = this.endMeasurement('tokenRefreshTime');

      if (duration > 2000) {
        console.warn(`Slow token refresh detected: ${duration}ms`);
      }
    });
  }

  /**
   * ユーザー同期のパフォーマンスを測定
   */
  measureUserSync<T>(callback: () => Promise<T>): Promise<T> {
    this.startMeasurement('userSyncTime');

    return callback().finally(() => {
      const duration = this.endMeasurement('userSyncTime');

      if (duration > 3000) {
        console.warn(`Slow user sync detected: ${duration}ms`);
      }
    });
  }

  /**
   * コンポーネントレンダリングのパフォーマンスを測定
   */
  measureComponentRender<T>(callback: () => T): T {
    this.startMeasurement('componentRenderTime');

    try {
      return callback();
    } finally {
      const duration = this.endMeasurement('componentRenderTime');

      if (duration > 16) {
        // 60fps = 16.67ms per frame
        console.warn(`Slow component render detected: ${duration}ms`);
      }
    }
  }

  /**
   * 最適化レポートを生成
   */
  generateOptimizationReport(): OptimizationReport {
    const recommendations: string[] = [];
    const issues: string[] = [];

    // メモリ使用量の分析
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
      const totalMB = Math.round(memory.totalJSHeapSize / 1048576);
      const limitMB = Math.round(memory.jsHeapSizeLimit / 1048576);

      if (usedMB > 100) {
        issues.push(`メモリ使用量が高い: ${usedMB}MB / ${totalMB}MB (制限: ${limitMB}MB)`);
        recommendations.push('コンポーネントのメモ化を実装してください');
        recommendations.push('非重要なコンポーネントの遅延読み込みを実装してください');
        recommendations.push('画像の遅延読み込みを最適化してください');
        recommendations.push('大きなリストには仮想スクロールを実装してください');
      } else {
        recommendations.push(`メモリ使用量は最適です: ${usedMB}MB`);
      }
    }

    // 認証状態変更の最適化チェック
    if (this.metrics.authStateChangeTime && this.metrics.authStateChangeTime > 500) {
      issues.push('認証状態変更が遅い');
      recommendations.push('認証状態の更新ロジックを最適化してください');
    }

    // トークンリフレッシュの最適化チェック
    if (this.metrics.tokenRefreshTime && this.metrics.tokenRefreshTime > 1000) {
      issues.push('トークンリフレッシュが遅い');
      recommendations.push('トークンリフレッシュの頻度を調整してください');
    }

    // ユーザー同期の最適化チェック
    if (this.metrics.userSyncTime && this.metrics.userSyncTime > 2000) {
      issues.push('ユーザー同期が遅い');
      recommendations.push('ユーザー同期処理を非同期化してください');
    }

    // コンポーネントレンダリングの最適化チェック
    if (this.metrics.componentRenderTime && this.metrics.componentRenderTime > 16) {
      issues.push('コンポーネントレンダリングが遅い');
      recommendations.push('React.memo や useMemo を使用してレンダリングを最適化してください');
    }

    // バンドルサイズの最適化提案
    recommendations.push('ルートベースのコンポーネントにコード分割を実装してください');
    recommendations.push('未使用の依存関係のツリーシェイキングを実装してください');
    recommendations.push('画像形式を最適化してください (WebP, AVIF)');

    return {
      metrics: this.metrics as PerformanceMetrics,
      recommendations,
      issues,
    };
  }

  /**
   * メモリ使用量を監視し、最適化の提案を行う
   */
  monitorMemoryUsage(): void {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
      const totalMB = Math.round(memory.totalJSHeapSize / 1048576);
      const limitMB = Math.round(memory.jsHeapSizeLimit / 1048576);

      // メモリ使用量の詳細ログ
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(`Memory Usage: ${usedMB}MB / ${totalMB}MB (Limit: ${limitMB}MB)`);
      }

      if (usedMB > 100) {
        // eslint-disable-next-line no-console
        console.warn(
          `High memory usage detected: ${usedMB}MB. Consider optimizing component state management.`
        );
        this.suggestMemoryOptimizations();
      }

      // メモリリークの検出
      this.detectMemoryLeaks();
    }
  }

  /**
   * メモリ最適化の提案
   */
  private suggestMemoryOptimizations(): void {
    const suggestions = [
      'Use React.memo for expensive components',
      'Implement useCallback and useMemo hooks',
      'Lazy load non-critical components',
      'Optimize image loading with lazy loading',
      'Clean up event listeners and subscriptions',
      'Use virtual scrolling for large lists',
      'Implement code splitting for route-based components',
    ];

    // eslint-disable-next-line no-console
    console.log('Memory optimization suggestions:', suggestions);
  }

  /**
   * メモリリークの検出
   */
  private detectMemoryLeaks(): void {
    // 定期的なメモリ使用量チェック
    if (!this.memoryCheckInterval) {
      this.memoryCheckInterval = window.setInterval(() => {
        if ('memory' in performance) {
          const memory = (performance as any).memory;
          const currentUsage = memory.usedJSHeapSize;

          if (this.lastMemoryUsage && currentUsage > this.lastMemoryUsage * 1.5) {
            console.warn('Potential memory leak detected. Memory usage increased significantly.');
          }

          this.lastMemoryUsage = currentUsage;
        }
      }, 30000); // 30秒ごとにチェック
    }
  }

  /**
   * 画像の遅延読み込み最適化
   */
  optimizeImageLoading(): void {
    // Intersection Observer APIを使用した画像の遅延読み込み
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      });

      // 遅延読み込み対象の画像を監視
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  /**
   * バンドルサイズの最適化チェック
   */
  analyzeBundleSize(): void {
    // 動的インポートの使用状況をチェック
    const lazyComponents = document.querySelectorAll('[data-lazy]');
    const totalLazyComponents = lazyComponents.length;

    if (totalLazyComponents > 0) {
      // eslint-disable-next-line no-console
      console.log(`Lazy loaded components: ${totalLazyComponents}`);
    }

    // 未使用のCSSクラスの検出
    this.detectUnusedCSS();
  }

  /**
   * 未使用のCSSクラスの検出
   */
  private detectUnusedCSS(): void {
    // 開発環境でのみ実行
    if (process.env.NODE_ENV === 'development') {
      const allClasses = new Set<string>();
      const usedClasses = new Set<string>();

      // 使用されているクラスを収集
      document.querySelectorAll('*').forEach(element => {
        element.classList.forEach(className => {
          usedClasses.add(className);
        });
      });

      // スタイルシートからクラスを収集
      Array.from(document.styleSheets).forEach(sheet => {
        try {
          Array.from(sheet.cssRules || []).forEach(rule => {
            if (rule instanceof CSSStyleRule) {
              allClasses.add(rule.selectorText);
            }
          });
        } catch (e) {
          // CORSエラーを無視
        }
      });

      const unusedClasses = Array.from(allClasses).filter(className => !usedClasses.has(className));

      if (unusedClasses.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(`Unused CSS classes detected: ${unusedClasses.length} classes`);
      }
    }
  }

  /**
   * ネットワークリクエストの最適化チェック
   */
  analyzeNetworkRequests(): void {
    if ('getEntriesByType' in performance) {
      const entries = performance.getEntriesByType('navigation');
      const networkEntries = Array.isArray(entries) ? entries : [];

      networkEntries.forEach(entry => {
        const navEntry = entry as PerformanceNavigationTiming;
        const loadTime = navEntry.loadEventEnd - navEntry.loadEventStart;

        if (loadTime > 3000) {
          console.warn(`Slow page load detected: ${loadTime}ms`);
        }
      });
    }
  }

  /**
   * 認証関連のローカルストレージ使用量をチェック
   */
  checkLocalStorageUsage(): void {
    let totalSize = 0;
    const authKeys = ['supabase.auth.token', 'user-preferences', 'auth-state'];

    authKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        totalSize += value.length;
      }
    });

    const sizeKB = Math.round(totalSize / 1024);

    if (sizeKB > 100) {
      // Consider cleaning up old data.
    }
  }

  /**
   * 全体的なパフォーマンス監査を実行
   */
  performAudit(): OptimizationReport {
    this.monitorMemoryUsage();
    this.analyzeNetworkRequests();
    this.checkLocalStorageUsage();
    this.optimizeImageLoading();
    this.analyzeBundleSize();

    return this.generateOptimizationReport();
  }

  /**
   * メトリクスをリセット
   */
  reset(): void {
    this.metrics = {};
    this.startTimes.clear();
  }
}

// シングルトンインスタンス
export const performanceOptimizer = new PerformanceOptimizer();

/**
 * パフォーマンス測定用のReact Hook
 */
export const usePerformanceMonitor = () => {
  const measureRender = <T>(callback: () => T): T => {
    return performanceOptimizer.measureComponentRender(callback);
  };

  const measureAsync = async <T>(
    operation: keyof PerformanceMetrics,
    callback: () => Promise<T>
  ): Promise<T> => {
    performanceOptimizer.startMeasurement(operation);

    try {
      return await callback();
    } finally {
      performanceOptimizer.endMeasurement(operation);
    }
  };

  return {
    measureRender,
    measureAsync,
    generateReport: () => performanceOptimizer.generateOptimizationReport(),
    performAudit: () => performanceOptimizer.performAudit(),
  };
};

export default PerformanceOptimizer;
